import React, { useState, useEffect } from 'react';
import { IconClose, IconCalculator, IconEuro, IconPercentage, IconInfo } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import { 
  DownPaymentFormData, 
  DownPayment,
  DownPaymentModalProps
} from '../../types/downPaymentTypes';
import { 
  createDownPayment, 
  calculateDownPaymentAmount, 
  validateDownPaymentInput, 
  formatCurrency, 
  downloadPdfBlob 
} from '../../api/downPaymentApi';

const DownPaymentModal: React.FC<DownPaymentModalProps> = ({
  isOpen,
  onClose,
  baseDocument,
  existingDownPayments = [],
  onDownPaymentCreated,
  format
}) => {
  const { currentUser } = useAuth();
  // M11b-3: advisor (read-only) darf keine Abschlagsrechnung erstellen.
  const isReadOnly = useIsReadOnly();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<DownPaymentFormData>({
    downPaymentType: 'percentage',
    downPaymentValue: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Berechnungen
  const totalExistingDownPayments = existingDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
  const remainingAmount = baseDocument.totalAmount - totalExistingDownPayments;
  const calculatedDownPaymentAmount = formData.downPaymentValue 
    ? calculateDownPaymentAmount(
        baseDocument.totalAmount, 
        formData.downPaymentType, 
        parseFloat(formData.downPaymentValue) || 0
      )
    : 0;
  
  // Debug-Ausgaben
  console.log('[DownPaymentModal] existingDownPayments received:', existingDownPayments);
  console.log('[DownPaymentModal] existingDownPayments.length:', existingDownPayments.length);
  console.log('[DownPaymentModal] totalExistingDownPayments:', totalExistingDownPayments);
  console.log('[DownPaymentModal] baseDocument.totalAmount:', baseDocument.totalAmount);
  console.log('[DownPaymentModal] remainingAmount:', remainingAmount);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        downPaymentType: 'percentage',
        downPaymentValue: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setError(null);
    }
  }, [isOpen]);

  const validation = formData.downPaymentValue 
    ? validateDownPaymentInput(
        baseDocument.totalAmount,
        existingDownPayments,
        formData.downPaymentType,
        parseFloat(formData.downPaymentValue) || 0
      )
    : { isValid: false };

  const handleInputChange = (field: keyof DownPaymentFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTypeChange = (type: 'amount' | 'percentage') => {
    setFormData(prev => ({ 
      ...prev, 
      downPaymentType: type,
      downPaymentValue: ''
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('Benutzer nicht authentifiziert');
      return;
    }

    const downPaymentValue = parseFloat(formData.downPaymentValue);
    if (!validation.isValid) {
      setError(validation.error || 'Ungültige Eingabe');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request = {
        baseDocumentId: baseDocument.id,
        baseDocumentType: baseDocument.type,
        downPaymentType: formData.downPaymentType,
        downPaymentValue: downPaymentValue,
        invoiceDate: formData.invoiceDate,
        notes: formData.notes || undefined,
        format: format,
        // Alle Formulardaten für die e-Rechnung-Generierung
        seller: baseDocument.seller,
        buyer: baseDocument.buyer,
        items: baseDocument.items,
        currency: baseDocument.currency,
        deliveryDate: baseDocument.deliveryDate,
        paymentTerms: baseDocument.paymentTerms,
        dueDate: baseDocument.dueDate,
        billingStartDate: baseDocument.billingStartDate,
        billingEndDate: baseDocument.billingEndDate,
        contractReference: baseDocument.contractReference,
        buyerOrderReference: baseDocument.buyerOrderReference,
        sellerOrderReference: baseDocument.sellerOrderReference,
        relatedOfferId: baseDocument.relatedOfferId,
        workflowReferenceId: baseDocument.workflowReferenceId,
        deliveryAddress: baseDocument.deliveryAddress
      };

      const token = await currentUser.getIdToken();
      const result = await createDownPayment(request, token);
      
      downloadPdfBlob(result.blob, result.filename);

      const newDownPayment = {
        id: 'temp_' + Date.now(),
        invoiceNumber: result.filename.replace('.pdf', ''),
        baseDocumentId: baseDocument.id,
        baseDocumentType: baseDocument.type,
        downPaymentType: formData.downPaymentType,
        downPaymentValue: downPaymentValue,
        amount: calculatedDownPaymentAmount,
        currency: baseDocument.currency,
        invoiceDate: formData.invoiceDate,
        status: 'issued' as const,
        notes: formData.notes,
        createdAt: new Date().toISOString()
      };

      onDownPaymentCreated(newDownPayment);
      onClose();

    } catch (err: any) {
      console.error('Fehler beim Erstellen der Abschlagsrechnung:', err);
      setError(err.message || 'Fehler beim Erstellen der Abschlagsrechnung');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-raised rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <div>
            <h2 className="text-xl font-semibold text-primary">Abschlagsrechnung erstellen</h2>
            <p className="text-muted text-sm mt-1">
              Für {baseDocument.type === 'offer' ? 'Angebot' : 'Rechnung'} {baseDocument.documentNumber}
            </p>
            <p className="text-[var(--tone-info-fg)] text-sm mt-1">
              Format: {format === 'zugferd' ? 'ZUGFeRD' : 'XRechnung'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-primary transition-colors"
          >
            <IconClose size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-hairline">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-surface-inset p-3 rounded">
              <p className="text-muted">Gesamtbetrag</p>
              <p className="text-primary font-medium">
                {formatCurrency(baseDocument.totalAmount, baseDocument.currency)}
              </p>
            </div>
            <div className="bg-surface-inset p-3 rounded">
              <p className="text-muted">Bereits abgerechnet</p>
              <p className="text-[var(--tone-success-fg)] font-medium">
                {formatCurrency(totalExistingDownPayments, baseDocument.currency)}
              </p>
            </div>
            <div className="bg-surface-inset p-3 rounded">
              <p className="text-muted">Noch offen</p>
              <p className="text-[var(--tone-warning-fg)] font-medium">
                {formatCurrency(remainingAmount, baseDocument.currency)}
              </p>
            </div>
          </div>
          
          {existingDownPayments.length > 0 && (
            <div className="mt-4 p-3 bg-[var(--tone-info-bg)] border border-blue-600/50 rounded">
              <p className="text-[var(--tone-info-fg)] text-sm flex items-center">
                <span className="mr-2">
                  <IconInfo />
                </span>
                Bereits {existingDownPayments.length} Abschlagsrechnung(en) erstellt
              </p>
              <div className="mt-2 text-xs text-[var(--tone-info-fg)]">
                {existingDownPayments.map((dp, index) => (
                  <div key={index} className="flex justify-between">
                    <span>#{dp.invoiceNumber}</span>
                    <span>{formatCurrency(dp.amount, dp.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary mb-3">
              Abschlagstyp
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleTypeChange('percentage')}
                className={`flex-1 p-3 rounded-lg border transition-colors ${
                  formData.downPaymentType === 'percentage'
                    ? 'bg-indigo-600 border-indigo-500 text-[#0C1322]'
                    : 'bg-surface-inset border-strong text-secondary hover:bg-surface-hover'
                }`}
              >
                <span className="mr-2">
                  <IconPercentage />
                </span>
                Prozentual
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('amount')}
                className={`flex-1 p-3 rounded-lg border transition-colors ${
                  formData.downPaymentType === 'amount'
                    ? 'bg-indigo-600 border-indigo-500 text-[#0C1322]'
                    : 'bg-surface-inset border-strong text-secondary hover:bg-surface-hover'
                }`}
              >
                <span className="mr-2">
                  <IconEuro />
                </span>
                Pauschalbetrag
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary mb-2">
              {formData.downPaymentType === 'percentage' ? 'Prozentsatz' : 'Betrag'}
            </label>
            <div className="relative">
              <input
                type="number"
                step={formData.downPaymentType === 'percentage' ? '0.1' : '0.01'}
                min="0"
                max={formData.downPaymentType === 'percentage' ? '99.9' : remainingAmount}
                value={formData.downPaymentValue}
                onChange={(e) => handleInputChange('downPaymentValue', e.target.value)}
                placeholder={formData.downPaymentType === 'percentage' ? 'z.B. 30' : 'z.B. 1500.00'}
                className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <div className="absolute right-3 top-2 text-muted">
                {formData.downPaymentType === 'percentage' ? '%' : '€'}
              </div>
            </div>
            
            {formData.downPaymentValue && calculatedDownPaymentAmount > 0 && (
              <div className="mt-2 p-3 bg-surface-inset rounded border-l-4 border-indigo-500">
                <div className="flex items-center text-sm">
                  <span className="mr-2 text-indigo-500">
                    <IconCalculator />
                  </span>
                  <span className="text-secondary">
                    Abschlagsbetrag: 
                  </span>
                  <span className="font-semibold text-primary ml-2">
                    {formatCurrency(calculatedDownPaymentAmount, baseDocument.currency)}
                  </span>
                </div>
                <div className="text-xs text-muted mt-1">
                  Verbleibend nach Abschlag: {formatCurrency(remainingAmount - calculatedDownPaymentAmount, baseDocument.currency)}
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary mb-2">
              Rechnungsdatum
            </label>
            <input
              type="date"
              value={formData.invoiceDate}
              onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Zusätzliche Hinweise zur Abschlagsrechnung..."
              rows={3}
              className="w-full px-3 py-2 bg-surface-inset border border-strong rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            />
          </div>

          {error && (
            <div className="mb-6 p-3 bg-[var(--tone-danger-bg)] border border-red-600/50 rounded text-[var(--tone-danger-fg)] text-sm">
              {error}
            </div>
          )}

          {!validation.isValid && validation.error && formData.downPaymentValue && (
            <div className="mb-6 p-3 bg-[var(--tone-warning-bg)] border border-yellow-600/50 rounded text-[var(--tone-warning-fg)] text-sm">
              {validation.error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-hover text-primary rounded-md hover:bg-gray-500 transition-colors"
              disabled={isLoading}
            >
              Abbrechen
            </button>
            {/* Abschlagsrechnung erstellen — M11b-3: für advisor (read-only) ausgeblendet */}
            {!isReadOnly && (
            <button
              type="submit"
              disabled={!validation.isValid || isLoading || !formData.downPaymentValue}
              className="flex-1 px-4 py-2 bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Erstelle...' : 'Abschlagsrechnung erstellen'}
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownPaymentModal; 